/**
 * Anzeige für `DocumentSubject`, wenn es maskiert sein kann (K1, Gatekeeper-
 * Bericht 09/Tor 1): `label`/`kind` sind `null`, wenn der Aufrufer das
 * Elternobjekt selbst nicht sieht — ein Grant kann direkt auf dem Dokument
 * liegen, ohne dass er das Elternobjekt einschließt. Dieselbe Rolle wie
 * `features/tasks/labels.ts::subjectLabel`.
 */

import type { DocumentRow, DocumentSubject, SenderRow } from '@/features/documents/api'

/**
 * „Eingang" für `subject === null`, sonst **`path_label`** vor `label` —
 * anders als die Reihenfolge in `features/tasks/labels.ts::subjectLabel`.
 * Die Liste verlangt es ausdrücklich so (Spec 09, „Die Liste": „subject.
 * path_label bzw. subject.label … Wohnung 3 gibt es zwölfmal"): Der Pfad
 * disambiguiert, das bloße Label nicht. Fällt `path_label` leer aus (der
 * unmittelbare Fall bei einem maskierten Vorfahren), greift `label`, sonst
 * der Platzhalter.
 */
export function documentSubjectLabel(subject: DocumentSubject | null): string {
  if (subject === null) return 'Eingang'
  if (subject.path_label !== '') return subject.path_label
  if (subject.label !== null) return subject.label
  return 'Objekt ohne Namen'
}

/** Die Ebene aus `subject.kind.label` — „Gebäude", „Grundstück", „Vertrag".
 * `null`, wenn `subject` `null` ist oder maskiert. */
export function documentSubjectKindLabel(subject: DocumentSubject | null): string | null {
  return subject?.kind?.label ?? null
}

/**
 * Spec 24b, Fachregel 7: der Kennsatz in Zeile (`document-row-nosearch-<id>`)
 * und Blatt (`document-text-source-hint`) — dieselbe Funktion für beide
 * Stellen, damit die Regel einmal steht. `null` heißt: kein Kennsatz (das
 * Dokument hat Text — `device`, `pdf_layer` oder `server_ocr`). Sonst
 * verbindet die Funktion zwei Serverwörter mit „ · "; beide Wörter kommen
 * ausschließlich aus `document.text_source.label`/`ocr_status.label` — die
 * Wörter selbst stehen nirgends im Client (Fachregel 7, „Zusammensetzen,
 * keine Fachlogik").
 */
export function documentSearchabilityHint(document: Pick<DocumentRow, 'text_source' | 'ocr_status'>): string | null {
  if (document.text_source.value !== 'none') return null
  if (document.ocr_status.value === 'done') return document.text_source.label
  return `${document.text_source.label} · ${document.ocr_status.label}`
}

/**
 * Spec 24c, Fachregel 5/9: der Kennsatz der Zeile ist entweder „Vorschlag"
 * (Akzent) oder „nicht durchsuchbar" (`muted`) — nie beide, ein Vorschlag
 * setzt Text voraus (Fachregel 3). Eine Funktion für beide Bedingungen,
 * damit die Ausschließlichkeit an einer Stelle steht statt an jeder
 * Aufruferzeile neu geprüft zu werden — dieselbe Begründung wie
 * `documentSearchabilityHint` selbst.
 */
export type DocumentHintBadge =
  | { readonly kind: 'suggestion'; readonly label: string }
  | { readonly kind: 'nosearch'; readonly label: string }
  | null

export function documentHintBadge(
  document: Pick<DocumentRow, 'review' | 'text_source' | 'ocr_status'>,
): DocumentHintBadge {
  if (document.review.value === 'needs_review') return { kind: 'suggestion', label: document.review.label }
  const hint = documentSearchabilityHint(document)
  return hint === null ? null : { kind: 'nosearch', label: hint }
}

/**
 * Spec 24c, Oberfläche B: die Absenderanzeige im geschlossenen Feld
 * (`document-sender`) und in der Zeile — `sender_ref` vor Freitext, mit
 * einem Merker für „(Freitext)" (Altbestand ohne `sender_id`). Fachregel 7
 * hält `sender` (Text) und `sender_ref.label` bei gesetztem `sender_id`
 * ohnehin gleich; diese Funktion entscheidet nur, welche der beiden Quellen
 * gilt und ob der Zusatz erscheint.
 */
export interface DocumentSenderDisplay {
  readonly label: string
  readonly isFreetext: boolean
}

export function documentSenderDisplay(
  document: Pick<DocumentRow, 'sender' | 'sender_ref'>,
): DocumentSenderDisplay | null {
  if (document.sender_ref !== null) return { label: document.sender_ref.label, isFreetext: false }
  if (document.sender !== null) return { label: document.sender, isFreetext: true }
  return null
}

/** Spec 24c, Oberfläche E: Standard-Art in `senders-list` — `default_
 * category.label` oder der Platzhalter „—". */
export function senderCategoryDisplay(sender: Pick<SenderRow, 'default_category'>): string {
  return sender.default_category?.label ?? '—'
}

/** Standard-Objekt in `senders-list`: „—" ohne Vorbelegung, sonst der Name
 * des Objekts (`senderSubjectLabel`). */
export function senderSubjectDisplay(sender: Pick<SenderRow, 'default_subject'>): string {
  return sender.default_subject === null ? '—' : senderSubjectLabel(sender.default_subject)
}

/** Name des Standard-Objekts selbst (Spec 24c, Oberfläche B:
 * `default_subject.label`) — `path_label` trägt nur die Vorfahren und greift
 * erst, wenn das Objekt maskiert ist (Tor B 24c, Berichte 32/35). */
export function senderSubjectLabel(subject: NonNullable<SenderRow['default_subject']>): string {
  if (subject.label !== null) return subject.label
  if (subject.path_label !== '') return subject.path_label
  return 'Objekt ohne Namen'
}

/**
 * Spec 24c, Oberfläche E: „2 IBAN · USt-ID · 1 Domain" — Zusammensetzen aus
 * Zahlen ohne Mehrzahlbildung, ausdrücklich als Ausnahme von „keine
 * Fachlogik im Client" erlaubt (Spec: „IBAN"/„Domain" bleiben unverändert).
 * Leer, wenn kein Merkmal gespeichert ist.
 */
export function senderIdentifiersSummary(sender: Pick<SenderRow, 'ibans' | 'vat_id' | 'email_domains'>): string {
  const parts: string[] = []
  if (sender.ibans.length > 0) parts.push(`${String(sender.ibans.length)} IBAN`)
  if (sender.vat_id !== null) parts.push('USt-ID')
  if (sender.email_domains.length > 0) parts.push(`${String(sender.email_domains.length)} Domain`)
  return parts.join(' · ')
}
